import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Server, Send, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminEmailPage() {
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: 'Pharmax.ai', use_tls: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [segment, setSegment] = useState('');
  const [toEmails, setToEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadSmtp(); }, []);

  const loadSmtp = async () => {
    try { const res = await adminAPI.getSmtp(); setSmtp(res.data); setValidated(res.data.validated || false); } catch {}
  };

  const saveSmtp = async () => {
    setSaving(true);
    try { await adminAPI.updateSmtp(smtp); toast.success('SMTP saved'); } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const testSmtp = async () => {
    setTesting(true);
    try { const res = await adminAPI.testSmtp(); res.data.ok ? (toast.success(res.data.message), setValidated(true)) : toast.error(`Failed: ${res.data.error}`); } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(false);
  };

  const sendEmail = async () => {
    if (!subject || !body) { toast.error('Subject and body required'); return; }
    if (!segment && !toEmails) { toast.error('Select a segment or enter emails'); return; }
    setSending(true);
    try {
      const data = { subject, body, segment: segment || undefined, to: toEmails ? toEmails.split(',').map(e => e.trim()) : [] };
      const res = await adminAPI.sendEmail(data);
      toast.success(`Sent to ${res.data.sent} recipients`);
      setSubject(''); setBody(''); setToEmails(''); setSegment('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[800px]" data-testid="admin-email">
      <h1 className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Email Center</h1>
      <p className="text-xs sm:text-sm text-muted-foreground mb-5">Configure SMTP and send emails to users</p>

      <Tabs defaultValue="smtp">
        <TabsList className="mb-4 w-full sm:w-auto">
          <TabsTrigger value="smtp" className="gap-1.5 text-xs flex-1 sm:flex-none"><Server className="w-3.5 h-3.5" /> SMTP</TabsTrigger>
          <TabsTrigger value="compose" className="gap-1.5 text-xs flex-1 sm:flex-none"><Send className="w-3.5 h-3.5" /> Compose</TabsTrigger>
        </TabsList>

        <TabsContent value="smtp">
          <Card>
            <CardHeader className="py-3 px-3 sm:px-4">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
                <Server className="w-4 h-4" /> SMTP Configuration
                {validated && <Badge variant="outline" className="ml-2 border-emerald-500/30 text-emerald-400 text-[9px]"><CheckCircle className="w-3 h-3 mr-0.5" /> OK</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-4 pb-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-[10px] sm:text-xs">SMTP Host</Label><Input value={smtp.host} onChange={(e) => setSmtp(s => ({ ...s, host: e.target.value }))} placeholder="smtp.gmail.com" className="mt-1 text-xs h-8" /></div>
                <div><Label className="text-[10px] sm:text-xs">Port</Label><Input type="number" value={smtp.port} onChange={(e) => setSmtp(s => ({ ...s, port: parseInt(e.target.value) || 587 }))} className="mt-1 text-xs h-8" /></div>
                <div><Label className="text-[10px] sm:text-xs">Username</Label><Input value={smtp.username} onChange={(e) => setSmtp(s => ({ ...s, username: e.target.value }))} className="mt-1 text-xs h-8" /></div>
                <div><Label className="text-[10px] sm:text-xs">Password</Label><Input type="password" value={smtp.password} onChange={(e) => setSmtp(s => ({ ...s, password: e.target.value }))} className="mt-1 text-xs h-8" /></div>
                <div><Label className="text-[10px] sm:text-xs">From Email</Label><Input value={smtp.from_email} onChange={(e) => setSmtp(s => ({ ...s, from_email: e.target.value }))} className="mt-1 text-xs h-8" /></div>
                <div><Label className="text-[10px] sm:text-xs">From Name</Label><Input value={smtp.from_name} onChange={(e) => setSmtp(s => ({ ...s, from_name: e.target.value }))} className="mt-1 text-xs h-8" /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp(s => ({ ...s, use_tls: v }))} /><Label className="text-xs">Use TLS</Label></div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveSmtp} disabled={saving} className="text-xs h-8">{saving ? 'Saving...' : 'Save'}</Button>
                <Button variant="outline" onClick={testSmtp} disabled={testing || !smtp.host} className="text-xs h-8">
                  {testing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing...</> : 'Send Test'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compose">
          <Card>
            <CardHeader className="py-3 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><Send className="w-4 h-4" /> Compose</CardTitle></CardHeader>
            <CardContent className="px-3 sm:px-4 pb-4 space-y-3">
              {!validated && <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 text-amber-400 text-[10px] sm:text-xs"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> Configure SMTP first.</div>}
              <div><Label className="text-[10px] sm:text-xs">Send To Segment</Label>
                <Select value={segment} onValueChange={setSegment}><SelectTrigger className="mt-1 text-xs h-8"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Manual only</SelectItem>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="active_7d">Active 7d</SelectItem>
                    <SelectItem value="active_30d">Active 30d</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] sm:text-xs">Additional Emails</Label><Input value={toEmails} onChange={(e) => setToEmails(e.target.value)} placeholder="a@x.com, b@y.com" className="mt-1 text-xs h-8" /></div>
              <div><Label className="text-[10px] sm:text-xs">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Updates from Pharmax.ai" className="mt-1 text-xs h-8" /></div>
              <div><Label className="text-[10px] sm:text-xs">Body</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message..." className="mt-1 text-xs" rows={6} /></div>
              <Button onClick={sendEmail} disabled={sending || !validated} className="text-xs h-8 w-full sm:w-auto">
                {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Send className="w-3 h-3 mr-1" /> Send Email</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
